/**
* Groups service — stateless API orchestration for the Groups feature.
*/
import { GROUP_STATUS } from '../../constants';
import { IS_DEV } from '../../config/env';
import type {
  Group,
  GroupDetails,
  GroupFormData,
  GroupPost,
  GroupSortOption,
} from '../../types';
import type {
  GroupMember,
  PendingApprovalRequest,
} from '../../types/groupDetails';
import groupsApi from './groupsApi';
import feedApi from '../feed/feedApi';
import {
  mapApiGroupRecordToGroup,
  mapApiListItemToGroup,
  mapApiMyGroupToGroup,
  mapFeedItemToGroupPost,
  mapFormToCreateFormData,
  mapFormToUpdateFormData,
  mapJoinRequestToPendingApproval,
  mapSortOptionToApi,
  normalizeGroupDetailPayload,
  parseNumericGroupId,
  type MappedGroupDetailBundle,
} from './groupsMapper';
import { GROUP_ROLE_ID, type ApiMyGroupItem } from './groupsTypes';

const DEFAULT_PAGE_SIZE = 50;

export interface FetchGroupsListParams {
  searchTerm?: string;
  pageNumber?: number;
  pageSize?: number;
  sortBy?: GroupSortOption | string;
}

export interface GroupDetailResult {
  data: GroupDetails;
  members: GroupMember[];
  posts: GroupPost[];
  meta: Pick<
    MappedGroupDetailBundle,
    | 'isOwner'
    | 'isAdmin'
    | 'requireJoinApproval'
    | 'requirePostApproval'
    | 'onlyAdminCanPost'
  >;
}

const unwrapMutationSuccess = (body: {
  isSuccess?: boolean;
  success?: boolean;
} | null | undefined): boolean =>
  body?.isSuccess === true || body?.success === true;

const groupsService = {
  getMyGroups: async (
    params: FetchGroupsListParams = {},
  ): Promise<{ data: Group[] }> => {
    const response = await groupsApi.listMyGroups({
      SearchTerm: params.searchTerm,
      PageNumber: params.pageNumber ?? 1,
      PageSize: params.pageSize ?? DEFAULT_PAGE_SIZE,
      SortBy: mapSortOptionToApi(params.sortBy),
    });
    const rawData = response.data as any;
    const items: ApiMyGroupItem[] = Array.isArray(rawData)
      ? rawData
      : Array.isArray(rawData?.items)
      ? rawData.items
      : Array.isArray(rawData?.data?.items)
      ? rawData.data.items
      : Array.isArray(rawData?.data)
      ? rawData.data
      : [];
    return { data: items.map(mapApiMyGroupToGroup) };
  },

  getAllGroups: async (
    params: FetchGroupsListParams = {},
  ): Promise<{ data: Group[]; hasNextPage?: boolean }> => {
    const response = await groupsApi.getGroups({
      SearchTerm: params.searchTerm,
      PageNumber: params.pageNumber ?? 1,
      PageSize: params.pageSize ?? DEFAULT_PAGE_SIZE,
      SortBy: mapSortOptionToApi(params.sortBy),
    });
    const page = response.data;
    const items = page?.items ?? [];
    return {
      data: items.map(item => mapApiListItemToGroup(item)),
      hasNextPage: page?.hasNextPage,
    };
  },

  searchGroupsList: async (
    searchTerm: string,
    isMyGroup: boolean,
  ): Promise<{ data: Group[] }> => {
    const response = await groupsApi.getGroupsList({
      isMyGroup,
      searchTerm,
    });
    const groups = response.data?.groups ?? [];
    return {
      data: groups.map(g => ({
        id: String(g.groupId),
        groupName: g.groupName,
        groupImage: g.iconUrl ?? null,
        totalMembers: g.membersCount ?? 0,
        status: g.isJoined ? GROUP_STATUS.JOINED : GROUP_STATUS.JOIN_NOW,
      })),
    };
  },

  getGroupDetails: async (groupId?: string): Promise<GroupDetailResult> => {
    const numericId = parseNumericGroupId(groupId);
    const response = await groupsApi.getGroupDetail({ groupId: numericId });
    const bundle = normalizeGroupDetailPayload(response.data, numericId);
    return {
      data: bundle.details,
      members: bundle.members,
      posts: bundle.posts,
      meta: {
        isOwner: bundle.isOwner,
        isAdmin: bundle.isAdmin,
        requireJoinApproval: bundle.requireJoinApproval,
        requirePostApproval: bundle.requirePostApproval,
        onlyAdminCanPost: bundle.onlyAdminCanPost,
      },
    };
  },

  getGroupPosts: async (groupId?: string): Promise<{ data: GroupPost[] }> => {
    if (groupId) {
      const detail = await groupsService.getGroupDetails(groupId);
      return { data: detail.posts };
    }

    const feedsResponse = await groupsApi.getFeeds({
      method: 'Groups',
      startIndex: 0,
      pageSize: DEFAULT_PAGE_SIZE,
      isMyGroup: true,
    });
    const feeds = feedsResponse.data?.feeds ?? [];
    return { data: feeds.map(mapFeedItemToGroupPost) };
  },

  getGroupJoinRequests: async (
    groupId: string,
    pageNumber = 1,
    pageSize = 20,
  ): Promise<{ data: PendingApprovalRequest[]; totalCount: number }> => {
    const response = await groupsApi.getGroupJoinRequests({
      groupId: parseNumericGroupId(groupId),
      pageNumber,
      pageSize,
      sortBy: 'Latest',
    });
    const envelope = response.data as {
      data?: {
        items?: Array<import('./groupsTypes').ApiGroupJoinRequestItem & Record<string, unknown>>;
        totalCount?: number;
      };
      items?: Array<import('./groupsTypes').ApiGroupJoinRequestItem & Record<string, unknown>>;
      totalCount?: number;
    };
    const page = envelope?.data;
    const items = page?.items ?? envelope?.items ?? [];
    const mapped = items
      .map(mapJoinRequestToPendingApproval)
      .filter((item): item is PendingApprovalRequest => item != null);
    return {
      data: mapped,
      totalCount: page?.totalCount ?? envelope?.totalCount ?? mapped.length,
    };
  },

  reviewJoinRequest: async (
    groupJoinRequestId: string | number,
    action: 'Approve' | 'Reject',
    reviewComments?: string,
  ): Promise<{ success: boolean; message?: string }> => {
    const numericId = parseNumericGroupId(groupJoinRequestId);
    if (numericId <= 0) {
      throw new Error('Invalid join request id');
    }

    // POST /api/v1/Groups/ReviewJoinRequest
    // Required: groupJoinRequestId, action ("Approve" | "Reject")
    // Optional: reviewComments
    const payload: import('./groupsTypes').ReviewJoinRequestPayload = {
      groupJoinRequestId: numericId,
      action,
    };

    const trimmedComments = reviewComments?.trim();
    if (trimmedComments) {
      payload.reviewComments = trimmedComments;
    }

    try {
      // Note: apiClient response interceptor rejects when body.success === false
      // (or on HTTP 4xx/5xx), so a thrown error here is expected for API failures —
      // it is not a hang or a missing await.
      const response = await groupsApi.reviewJoinRequest(payload);
      const body = response.data;

      if (body && body.success === false) {
        throw {
          response: {
            status: typeof body.status === 'number' ? body.status : 400,
            data: body,
          },
          message: body.message || 'Failed to review join request',
        };
      }

      return { success: true, message: body?.message };
    } catch (error: unknown) {
      if (IS_DEV) {
        const err = error as {
          message?: string;
          response?: { status?: number; data?: unknown };
        };
        console.warn('[Groups] ReviewJoinRequest failed', {
          payload,
          status: err?.response?.status,
          data: err?.response?.data,
          message: err?.message,
        });
      }
      throw error;
    }
  },

  joinGroup: async (
    groupId: string,
    options?: { requireJoinApproval?: boolean },
  ): Promise<{ success: boolean; status: Group['status'] }> => {
    await groupsApi.joinGroup({ groupId: parseNumericGroupId(groupId) });
    const status =
      options?.requireJoinApproval === true
        ? GROUP_STATUS.PENDING
        : GROUP_STATUS.JOINED;
    return { success: true, status };
  },

  cancelJoinRequest: async (groupId: string): Promise<{ success: boolean }> => {
    await groupsApi.cancelJoinRequest({
      groupId: parseNumericGroupId(groupId),
    });
    return { success: true };
  },

  leaveGroup: async (groupId?: string): Promise<{ success: boolean }> => {
    if (!groupId) return { success: false };
    await groupsApi.leaveGroup({ groupId: parseNumericGroupId(groupId) });
    return { success: true };
  },

  deleteGroup: async (groupId?: string): Promise<{ success: boolean }> => {
    if (!groupId) return { success: false };
    await groupsApi.deleteGroup({ groupId: parseNumericGroupId(groupId) });
    return { success: true };
  },

  createGroup: async (
    groupData: GroupFormData,
  ): Promise<{
    success: boolean;
    data: Group | null;
    imageUploadPending?: boolean;
  }> => {
    const payload = mapFormToCreateFormData(groupData);
    const response = await groupsApi.createGroup(payload);
    const body = response.data;

    const createdRecord = body?.data;

    if (unwrapMutationSuccess(body) && createdRecord) {
      return {
        success: true,
        data: mapApiGroupRecordToGroup(createdRecord, GROUP_STATUS.OWNER),
        imageUploadPending: false,
      };
    }

    if (unwrapMutationSuccess(body)) {
      return { success: true, data: null, imageUploadPending: false };
    }

    return { success: false, data: null, imageUploadPending: false };
  },

  updateGroup: async (
    groupId: string,
    groupData: GroupFormData,
  ): Promise<{
    success: boolean;
    data: Group | null;
    imageUploadPending?: boolean;
  }> => {
    const payload = mapFormToUpdateFormData(groupId, groupData);
    const imageUploadPending = false; // We upload binary directly now
    const response = await groupsApi.updateGroup(payload);
    const body = response.data;

    if (unwrapMutationSuccess(body) && body?.data) {
      return {
        success: true,
        data: mapApiGroupRecordToGroup(body.data, GROUP_STATUS.JOINED),
        imageUploadPending,
      };
    }

    if (unwrapMutationSuccess(body) || response.status === 204) {
      return { success: true, data: null, imageUploadPending };
    }

    return { success: false, data: null, imageUploadPending };
  },

  changeMemberRole: async (
    groupId: string,
    userId: number,
    newRoleId: number,
  ): Promise<{ success: boolean }> => {
    await groupsApi.changeMemberRole({
      groupId: parseNumericGroupId(groupId),
      userId,
      newRoleId: newRoleId as (typeof GROUP_ROLE_ID)[keyof typeof GROUP_ROLE_ID],
    });
    return { success: true };
  },

  removeMember: async (
    groupId: string,
    userId: number,
  ): Promise<{ success: boolean }> => {
    await groupsApi.removeMember({
      groupId: parseNumericGroupId(groupId),
      userId,
    });
    return { success: true };
  },

  addMember: async (
    groupId: string,
    userId: number,
    roleId: number = GROUP_ROLE_ID.Member,
  ): Promise<{ success: boolean }> => {
    await groupsApi.addMember({
      groupId: parseNumericGroupId(groupId),
      userId,
      roleId: roleId as (typeof GROUP_ROLE_ID)[keyof typeof GROUP_ROLE_ID],
    });
    return { success: true };
  },

  likeGroupPost: async (postId?: string, reactionTypeId: number = 1): Promise<{ success: boolean }> => {
    if (!postId) return { success: false };
    await groupsApi.postReaction({
      postId: parseNumericGroupId(postId),
      reactionTypeId,
    });
    return { success: true };
  },

  saveGroupPost: async (
    postId?: string,
    isSave: boolean = true,
  ): Promise<{ success: boolean; postId?: string }> => {
    if (!postId) {
      throw new Error('Missing postId');
    }
    const response = await feedApi.saveUnsavePost({
      postId: Number(postId),
      isSave,
    });
    if (response.status === 200 && response.data?.success !== false) {
      return { success: true, postId };
    }
    const message =
      typeof response.data?.message === 'string' && response.data.message.trim()
        ? response.data.message
        : 'Failed to save post';
    throw new Error(message);
  },
};

export default groupsService;
 